import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  deleteOrganisation,
  getOrganisationById,
  previewReleaseAutomationChanges,
  upgradeOrganisation,
  updateOrganisation,
  updateOrganisationSettings,
} from '@/features/organisations/data';
import { getOrganisationEntitlements } from '@/shared/data/entitlements';
import { useAccessToken } from './auth';
import { gridPaths, invalidateGridQuery, queryKeys } from './queryKeys';
import { useInvalidateQueries } from './useInvalidateQueries';
import type { ApiError } from '@/shared/data/client';
import type { OrganisationDto, OrganisationEntitlementsDto } from '@flowqa/shared';

export const useOrganisationDetailQuery = (
  id: string | null,
): UseQueryResult<OrganisationDto | null, ApiError> => {
  const token = useAccessToken();
  return useQuery<OrganisationDto | null, ApiError>({
    queryKey: queryKeys.organisationDetail(id),
    enabled: Boolean(token && id),
    queryFn: async () => (id ? await getOrganisationById(id, token ?? undefined) : null),
    staleTime: 30_000,
  });
};

export const useOrganisationEntitlements = (
  id: string | null,
): UseQueryResult<OrganisationEntitlementsDto | null, ApiError> => {
  const token = useAccessToken();
  return useQuery<OrganisationEntitlementsDto | null, ApiError>({
    queryKey: queryKeys.organisationEntitlements(id),
    enabled: Boolean(token && id),
    queryFn: async () => (id ? await getOrganisationEntitlements(id, token ?? undefined) : null),
    staleTime: 30_000,
  });
};

export const useUpdateOrganisationSettings = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  {
    id: string;
    automateReleaseStatuses: boolean;
    requireFeaturesForReady: boolean;
    requireReadyFeaturesForReady: boolean;
    recomputeReleaseStatuses?: boolean;
    recomputeReleaseIds?: string[];
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<
    OrganisationDto,
    ApiError,
    {
      id: string;
      automateReleaseStatuses: boolean;
      requireFeaturesForReady: boolean;
      requireReadyFeaturesForReady: boolean;
      recomputeReleaseStatuses?: boolean;
      recomputeReleaseIds?: string[];
    }
  >({
    mutationFn: async (input) => await updateOrganisationSettings(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useUpdateOrganisation = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  { id: string; name: string }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<OrganisationDto, ApiError, { id: string; name: string }>({
    mutationFn: async (input) => await updateOrganisation(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useDeleteOrganisation = (): UseMutationResult<
  { status: string },
  ApiError,
  { id: string }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<{ status: string }, ApiError, { id: string }>({
    mutationFn: async (input) => await deleteOrganisation(input),
    onSuccess: async (_payload, input) => {
      queryClient.removeQueries({ queryKey: queryKeys.organisationDetail(input.id) });
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
    },
  });
};

export const useReleaseAutomationPreview = (): UseMutationResult<
  {
    items: Array<{
      id: string;
      slug: string;
      name: string;
      projectId: string;
      projectName: string;
      currentStatus: string;
      nextStatus: string;
    }>;
  },
  ApiError,
  {
    id: string;
    automateReleaseStatuses: boolean;
    requireFeaturesForReady: boolean;
    requireReadyFeaturesForReady: boolean;
  }
> => {
  return useMutation({
    mutationFn: async (input) => await previewReleaseAutomationChanges(input),
  });
};

export const useUpgradeOrganisation = (): UseMutationResult<
  OrganisationDto,
  ApiError,
  {
    id: string;
    name: string;
    planCode: OrganisationEntitlementsDto['plan']['code'];
    addOns?: { deliveryEnabled?: boolean; operationsEnabled?: boolean };
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  return useMutation<
    OrganisationDto,
    ApiError,
    {
      id: string;
      name: string;
      planCode: OrganisationEntitlementsDto['plan']['code'];
      addOns?: { deliveryEnabled?: boolean; operationsEnabled?: boolean };
    }
  >({
    mutationFn: async (input) => await upgradeOrganisation(input),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.organisationDetail(updated.id), updated);
      await invalidateQueries([invalidateGridQuery(gridPaths.organisations)]);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.organisationEntitlements(updated.id),
      });
    },
  });
};
